import Phaser from 'phaser';
import { WALL_X, WALL_WIDTH } from '../config';
import { Enemy } from '../objects/Enemy';

/**
 * Animated auto-click cursor that moves across the battlefield
 * targeting enemies. Granted by "The New Beginning" prestige tiers.
 */
export class AutoClicker {
    private scene: Phaser.Scene;
    private cursor: Phaser.GameObjects.Graphics;
    private interval: number = 0; // ms between clicks (0 = disabled)
    private timer: number = 0;
    private targetX: number = 0;
    private targetY: number = 0;
    private moveSpeed: number = 600; // px/sec cursor movement speed
    private onClickCallback: (x: number, y: number) => void;
    enabled: boolean = false;

    constructor(scene: Phaser.Scene, onClickCallback: (x: number, y: number) => void) {
        this.scene = scene;
        this.onClickCallback = onClickCallback;

        // Create persistent cursor graphic
        this.cursor = scene.add.graphics();
        this.cursor.setDepth(1000);
        this.cursor.setVisible(false);
        this.targetX = WALL_X + WALL_WIDTH + 200;
        this.targetY = 300;
        this.drawCursor();
    }

    setInterval(ms: number): void {
        this.interval = ms;
        this.enabled = ms > 0;
        this.cursor.setVisible(this.enabled);
        if (this.enabled) {
            this.timer = ms;
        }
    }

    update(delta: number, enemies: Enemy[]): void {
        if (!this.enabled || this.interval <= 0) return;

        // Find nearest living enemy on the battlefield
        const target = this.findTarget(enemies);
        if (!target) return;

        // Update target position
        this.targetX = target.x;
        this.targetY = target.y;

        // Move cursor toward target
        const curPos = this.cursor.getData('pos') as { x: number; y: number } || { x: WALL_X + 200, y: 300 };
        const dx = this.targetX - curPos.x;
        const dy = this.targetY - curPos.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist > 5) {
            const step = this.moveSpeed * (delta / 1000);
            const ratio = Math.min(step / dist, 1);
            curPos.x += dx * ratio;
            curPos.y += dy * ratio;
        } else {
            curPos.x = this.targetX;
            curPos.y = this.targetY;
        }

        this.cursor.setData('pos', curPos);
        this.cursor.setPosition(curPos.x, curPos.y);
        this.cursor.setVisible(true);

        // Count down to click
        this.timer -= delta;
        if (this.timer <= 0 && dist < 30) {
            this.timer = this.interval;
            this.onClickCallback(curPos.x, curPos.y);

            // Click animation: brief scale pulse
            this.scene.tweens.add({
                targets: this.cursor,
                scaleX: 1.3,
                scaleY: 1.3,
                duration: 80,
                yoyo: true,
                ease: 'Power1',
            });
        }
    }

    private findTarget(enemies: Enemy[]): Enemy | null {
        let nearest: Enemy | null = null;
        let minDist = Infinity;
        const wallEdge = WALL_X + WALL_WIDTH;

        for (const enemy of enemies) {
            if (enemy.isDead) continue;
            if (enemy.enemyType === 'boulder') continue;
            if (enemy.isBurrowed || enemy.isGoblin) continue; // Ignore burrowed enemies and loot goblins
            if (enemy.x < wallEdge) continue; // Only target enemies on the field
            const dx = enemy.x - (this.cursor.getData('pos')?.x || wallEdge + 200);
            const dy = enemy.y - (this.cursor.getData('pos')?.y || 300);
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < minDist) {
                minDist = dist;
                nearest = enemy;
            }
        }
        return nearest;
    }

    private drawCursor(): void {
        this.cursor.clear();

        // White mouse cursor with black outline
        const color = 0xffffff;
        this.cursor.fillStyle(color, 0.9);
        this.cursor.lineStyle(1.5, 0x000000, 1);

        this.cursor.beginPath();
        // Arrow cursor shape
        this.cursor.moveTo(0, 0);
        this.cursor.lineTo(0, 18);
        this.cursor.lineTo(4, 14);
        this.cursor.lineTo(9, 20);
        this.cursor.lineTo(11, 19);
        this.cursor.lineTo(6, 12);
        this.cursor.lineTo(12, 12);
        this.cursor.closePath();

        this.cursor.fillPath();
        this.cursor.strokePath();

        // Slight rotation for natural look
        this.cursor.setRotation(-0.35);
        this.cursor.setData('pos', { x: WALL_X + WALL_WIDTH + 200, y: 300 });
    }

    destroy(): void {
        this.cursor.destroy();
    }
}
